import { Button } from '@1trading/react/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Loading } from '@1trading/react/components/ui/loading';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { useToast } from '@1trading/react/hooks/use-toast';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingTitle } from '@/components/ui/page-heading';
import { CONTROL_TYPES, ControlType } from '@/constants/institution';
import InstitutionFuturesAlgorithm from '@/features/institution/futures/components/algorithm';
import InstitutionFuturesIndividuals from '@/features/institution/futures/components/individuals';
import {
  type InstitutionFuturesDecisionMaker,
  type InstitutionFuturesDecisionMakerOrDefaults,
  institutionDetailsDecisionMakerApiToInstitutionFuturesDecisionMaker,
  institutionFuturesDecisionMakerSchema,
  institutionFuturesDecisionMakerToInstitutionDetailsDecisionMakerApi,
} from '@/features/institution/futures/decision-maker/schema';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { reportIndividualNationalClientIdentifierFormatErrors } from '@/features/institution/schemas/individual';
import { INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import { useProfileInstitutionDetailsAnswers, useProfileInstitutionDetailsMutation } from '@/hooks/queries/use-profile-institution-details';

const InstitutionFuturesDecisionMakerFormForm = ({ defaultValues }: { defaultValues: InstitutionFuturesDecisionMakerOrDefaults }) => {
  const form = useForm<InstitutionFuturesDecisionMaker>({
    defaultValues,
    resolver: standardSchemaResolver(institutionFuturesDecisionMakerSchema),
  });
  const { isPending, mutate } = useProfileInstitutionDetailsMutation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const decisionMaker = form.watch('decisionMaker');

  const onSubmit = (data: InstitutionFuturesDecisionMaker) => {
    mutate(
      { decisionMaker: institutionFuturesDecisionMakerToInstitutionDetailsDecisionMakerApi(data) },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          navigate('/institution/futures/execution', { replace: true });
        },
      },
    );
  };

  const onInvalid = () => reportIndividualNationalClientIdentifierFormatErrors(form.getValues('individuals'));

  return (
    <Form {...form}>
      <form name="institution-decision-maker" onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
        <PageCardContent className="pt-8 space-y-6">
          <FormField
            name="decisionMaker"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="sr-only">{t('institutionDetails.decisionMaker.decisionMaker')}</FormLabel>
                <FormControl>
                  <RadioGroup
                    defaultValue={field.value}
                    variant="cards"
                    aria-label={t('institutionDetails.decisionMaker.decisionMaker')}
                    onValueChange={(value) => {
                      field.onChange(value);
                      (form as UseFormReturn<InstitutionFuturesDecisionMakerOrDefaults>).setValue(value === ControlType.INDIVIDUAL ? 'algorithm' : 'individuals', undefined);
                    }}
                  >
                    {CONTROL_TYPES.map((answer) => (
                      <FormItem key={answer}>
                        <RadioGroupItemCard variant="outline">
                          <FormControl>
                            <RadioGroupItem value={answer} />
                          </FormControl>
                          <FormLabel contained>{t(`institutionDetails.controlType.options.${answer}`)}</FormLabel>
                        </RadioGroupItemCard>
                      </FormItem>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {{
            [ControlType.ALGORITHM]: <InstitutionFuturesAlgorithm />,
            [ControlType.INDIVIDUAL]: <InstitutionFuturesIndividuals section="decisionMaker" />,
          }[decisionMaker] ?? null}
        </PageCardContent>
        <PageCardFooter>
          <Button className="relative w-full" size="lg" type="submit" variant="primary" disabled={isPending}>
            <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
            {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
          </Button>
        </PageCardFooter>
      </form>
    </Form>
  );
};
InstitutionFuturesDecisionMakerFormForm.displayName = 'InstitutionFuturesDecisionMakerFormForm';

const InstitutionFuturesDecisionMakerForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useProfileInstitutionDetailsAnswers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.FUTURES, 'decisionMaker');
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;

  if (isLoading) return <LoadingCard />;

  if (!institutionDetailsAnswers?.company?.leiNumber) return <Navigate to="/institution/futures/company-information" replace />;

  const defaultValues = institutionDetailsAnswers?.decisionMaker
    ? institutionDetailsDecisionMakerApiToInstitutionFuturesDecisionMaker(institutionDetailsAnswers.decisionMaker)
    : {};

  return (
    <section className="relative">
      <PageHeading>
        <PageHeadingTitle>{t('institutionDetails.decisionMaker.title')}</PageHeadingTitle>
      </PageHeading>
      <PageCard size="lg" bordered fixedFooter>
        <InstitutionFuturesDecisionMakerFormForm defaultValues={defaultValues} />
      </PageCard>
    </section>
  );
};
InstitutionFuturesDecisionMakerForm.displayName = 'InstitutionFuturesDecisionMakerForm';

export default InstitutionFuturesDecisionMakerForm;
